import { Organization } from '@quadminds/client-apis/auth';
import { Typography } from '@quadminds/ui-kit';
import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import OrganizationCard from './OrganizationCard';
import defaultOrgPhoto from '../../assets/default-image-form.svg';
import { isFleetcare, isMindy, isOms } from '../../utils';

// OMS usa la paleta azul (oms.variables.css), el resto el violeta de Mindy
const HOVER_BORDER_COLOR = isOms ? 'rgb(59 130 246)' : 'rgb(139 92 246)';

export type SelectOrganizationComponentProps = {
    organizations: Organization[];
    selectedOrganization: string | null;
    selectHandler: (id: string) => void;
    isSelectOrgLoading: boolean;
};

export function SelectOrganizationComponent({
    organizations,
    selectHandler,
    selectedOrganization,
    isSelectOrgLoading = false,
}: SelectOrganizationComponentProps) {
    const { t } = useTranslation();

    if (isMindy || isFleetcare || isOms) {
        return (
            <div className="w-full space-y-6">
                {/* Header */}
                <div>
                    <h2 className="text-2xl font-poppins font-semibold text-white">
                        {t('selectOrganizationComponent.title')}
                    </h2>
                    <p className="text-sm text-gray-400 font-roboto mt-1">
                        {t('selectOrganizationComponent.subTitle')}
                    </p>
                    <div className="border-t border-gray-700 mt-4" />
                </div>

                {/* Organization list */}
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    {organizations.map((org, idx) => (
                        <motion.div
                            key={org.id}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            whileHover={{ borderColor: HOVER_BORDER_COLOR }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ duration: 0.3, delay: idx * 0.07 }}
                            onClick={() => selectHandler(org.id)}
                            className="bg-gray-900 border border-gray-700 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-800 transition-colors"
                        >
                            <img
                                src={org.photo || defaultOrgPhoto}
                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                    (e.target as HTMLImageElement).src = defaultOrgPhoto;
                                }}
                                alt={org.name}
                                className="w-14 h-14 rounded-full object-cover ring-2 ring-primary-500 flex-shrink-0"
                            />
                            <div className="flex flex-col overflow-hidden">
                                <span className="text-white font-poppins font-semibold text-base truncate">
                                    {org.name}
                                </span>
                            </div>
                            {isSelectOrgLoading && org.id === selectedOrganization && (
                                <span className="ml-auto inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            )}
                        </motion.div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div
            data-test-id="select-organization-component"
            className="mt-8 mb-10 w-full px-8 max-w-[90%] lg:max-w-[900px] mx-auto min-h-[580px] rounded-xl bg-white flex flex-col justify-center items-center z-10"
        >
            <Typography
                variant="headline-2"
                className="text-center mt-10 text-surface-high font-semibold"
                font="roboto"
                value={t('selectOrganizationComponent.title')}
            />
            <Typography
                variant="headline-5"
                className="text-center mt-2 text-gray-400 font-normal"
                value={t('selectOrganizationComponent.subTitle')}
            />
            <div className="flex flex-wrap justify-center gap-4 p-8 w-full">
                {organizations.map((org, idx) => (
                    <OrganizationCard
                        organization={org}
                        selectHandler={selectHandler}
                        selectedOrganization={selectedOrganization}
                        isSelectOrgLoading={isSelectOrgLoading}
                        key={`${org.id}-${idx}`}
                    />
                ))}
            </div>
        </div>
    );
}

export default SelectOrganizationComponent;
